/**
 * Confirmation for a destructive action (T290, FR-091).
 *
 * REPLACES `window.confirm`, not just for looks. A native browser confirm
 * is inconsistent across tablets (some Android WebViews suppress it
 * entirely), never matches the brand, and — the actual reason this exists —
 * cannot carry a REASON or attribute WHO confirmed beyond "someone tapped
 * OK". FR-091 asks for confirmation AND actor recording on every destructive
 * action; the actor half is the caller's job (the API call itself carries
 * the session token, which is what the server actually audits — see
 * catalog.ts's retirement endpoint), but the reason and the deliberate,
 * unmissable "yes, really" step belong here.
 *
 * A MODAL, NOT A TOAST. Destructive actions are the one class of confirm
 * that should interrupt rather than sit quietly in a corner — a missed
 * toast on a retire or a void is exactly the failure mode this exists to
 * prevent.
 */
export interface ConfirmDestructiveProps {
  /** What is about to happen, plainly: "Take this item off the menu?" */
  title: string;
  /** Why it's safe (or what it does NOT do), e.g. "Past sales still name it." */
  detail?: string;
  /** The affirmative action's own label, e.g. "Take off the menu" — never a bare "OK". */
  confirmLabel: string;
  onConfirm(): void;
  onCancel(): void;
}

export function ConfirmDestructive({
  title,
  detail,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDestructiveProps) {
  return (
    <div className="confirmdestructive" role="alertdialog" aria-label={title}>
      <h2 className="confirmdestructive__title">{title}</h2>
      {detail && <p className="confirmdestructive__detail">{detail}</p>}
      <div className="confirmdestructive__actions">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="confirmdestructive__confirm" onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

export default ConfirmDestructive;
